import { firebaseConfig, isPushConfigured, serviceWorkerUrl, vapidKey } from './config';

/**
 * Thin wrapper around the Firebase Web SDK.
 *
 * Everything is dynamically imported: the SDK is ~200 kB and only matters once
 * an operator opts into push, so it must stay out of the initial bundle. Every
 * function no-ops (returning null) when push is unconfigured or unsupported,
 * so callers never need to guard.
 */

let messagingPromise = null;
let swRegistration = null;

/** SW + Push API + Notification API + FCM's own support check. */
export const isPushSupported = async () => {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (!('Notification' in window)) return false;
  if (!isPushConfigured()) return false;

  try {
    const { isSupported } = await import('firebase/messaging');
    return await isSupported();
  } catch {
    return false;
  }
};

export const getPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

/**
 * Prompts only while the permission is still `default`. Browsers permanently
 * ignore repeat prompts after a denial, so a denied state is returned as-is
 * for the UI to explain rather than re-asked.
 */
export const requestPermission = async () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
};

export const registerServiceWorker = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  if (swRegistration) return swRegistration;
  try {
    swRegistration = await navigator.serviceWorker.register(serviceWorkerUrl(), { scope: '/' });
    // getToken() needs an *active* worker; a fresh registration is still
    // installing at this point.
    await navigator.serviceWorker.ready;
    return swRegistration;
  } catch (err) {
    console.warn('[push] service worker registration failed', err);
    return null;
  }
};

const getMessaging = () => {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      if (!(await isPushSupported())) return null;
      try {
        const [{ getApps, initializeApp }, { getMessaging: init }] = await Promise.all([
          import('firebase/app'),
          import('firebase/messaging')
        ]);
        const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
        return init(app);
      } catch (err) {
        console.warn('[push] firebase messaging init failed', err);
        return null;
      }
    })();
  }
  return messagingPromise;
};

/**
 * Current FCM registration token, or null.
 *
 * FCM rotates tokens on its own schedule and the v9+ SDK has no
 * `onTokenRefresh` — calling this on every app start and re-POSTing the result
 * under the unchanged device_id is the supported way to stay current.
 */
export const getPushToken = async () => {
  const messaging = await getMessaging();
  if (!messaging) return null;
  if (getPermission() !== 'granted') return null;

  const registration = await registerServiceWorker();
  if (!registration) return null;

  try {
    const { getToken } = await import('firebase/messaging');
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
    return token || null;
  } catch (err) {
    console.warn('[push] getToken failed', err);
    return null;
  }
};

/** Revokes the token client-side. Pair with the server-side deactivate call. */
export const deletePushToken = async () => {
  const messaging = await getMessaging();
  if (!messaging) return;
  try {
    const { deleteToken } = await import('firebase/messaging');
    await deleteToken(messaging);
  } catch (err) {
    console.warn('[push] deleteToken failed', err);
  }
};

/**
 * Foreground pushes. While a tab is focused FCM does NOT raise a system
 * notification — the payload arrives here and the app must surface it.
 */
export const onForegroundMessage = (handler) => {
  let unsubscribe = null;
  let cancelled = false;

  (async () => {
    const messaging = await getMessaging();
    if (!messaging || cancelled) return;
    const { onMessage } = await import('firebase/messaging');
    unsubscribe = onMessage(messaging, handler);
  })();

  return () => {
    cancelled = true;
    if (unsubscribe) unsubscribe();
  };
};
